import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Ticket, Trophy } from "lucide-react";
import type { ProductDto } from "@gaole/shared";
import { api } from "../lib/api";
import { EASE } from "../lib/motion";
import { ProductImage } from "../components/ProductCard";
import { RaffleCard, WinnerSpotlight } from "../components/storefront/RaffleCard";
import { PageHeader } from "../components/storefront/PageHeader";
import { SectionHeader } from "../components/storefront/SectionHeader";
import { EmptyState, PageLoader } from "../components/ui";
import { Reveal } from "../components/fx";

interface RaffleListItem {
  id: string;
  title: string;
  status: string;
  entryOpensAt: string;
  entryClosesAt: string;
  ticketCount: number;
  priceForWinner: number;
  winnerName: string | null;
  product: ProductDto;
}

export function Raffles() {
  const { data, isLoading } = useQuery({
    queryKey: ["raffles"],
    queryFn: () => api<{ items: RaffleListItem[] }>("/raffles"),
    refetchInterval: 30_000,
  });

  if (isLoading) return <PageLoader />;

  const items = data?.items ?? [];
  const live = items.filter((r) => ["OPEN", "DRAWING"].includes(r.status));
  const upcoming = items.filter((r) => r.status === "SCHEDULED");
  const finished = items.filter((r) => ["DRAWN", "COMPLETED"].includes(r.status));
  const latestWinner = finished.find((r) => r.winnerName);
  const [hero, ...restLive] = live;

  return (
    <div className="px-4 pb-10 pt-4 lg:px-6">
      <div className="lg:grid lg:grid-cols-[minmax(0,1fr)_300px] lg:gap-8">
        {/* ---------- main column ---------- */}
        <div className="space-y-6">
          <PageHeader
            title="Raffles"
            description="Grab a ticket, watch the drum spin live, and win the right to buy a grail at a fixed price."
          />

          {items.length === 0 && (
            <EmptyState icon={Ticket} title="No raffles right now" hint="Follow us — lobbies open regularly." />
          )}

          {hero && (
            <Reveal as="section" className="space-y-4">
              <SectionHeader title="Live now" hint="entries open" />
              <RaffleCard raffle={hero} variant="hero" />
              {restLive.length > 0 && (
                <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                  {restLive.map((raffle, i) => (
                    <motion.div
                      key={raffle.id}
                      initial={{ opacity: 0, y: 16 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: (i % 3) * 0.06, duration: 0.4, ease: EASE.outExpo }}
                    >
                      <RaffleCard raffle={raffle} variant="compact" />
                    </motion.div>
                  ))}
                </div>
              )}
            </Reveal>
          )}

          {upcoming.length > 0 && (
            <Reveal as="section" className="space-y-3">
              <SectionHeader title="Opening soon" hint="set a reminder" />
              <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                {upcoming.map((raffle) => (
                  <RaffleCard key={raffle.id} raffle={raffle} variant="compact" />
                ))}
              </div>
            </Reveal>
          )}

          {latestWinner ? (
            <Reveal>
              <WinnerSpotlight raffle={latestWinner} />
            </Reveal>
          ) : null}

          {/* recent winners — mobile placement */}
          {finished.length > 0 && (
            <section className="space-y-2 lg:hidden">
              <h2 className="font-display text-xs font-semibold uppercase tracking-widest text-white/40">
                Recent winners
              </h2>
              {finished.slice(0, 6).map((raffle) => (
                <WinnerRow key={raffle.id} raffle={raffle} />
              ))}
            </section>
          )}
        </div>

        {/* ---------- desktop winners rail ---------- */}
        {finished.length > 0 && (
          <aside className="hidden lg:block">
            <div className="sticky top-24">
              <h2 className="mb-3 font-display text-xs font-semibold uppercase tracking-widest text-white/40">
                Recent winners
              </h2>
              <div className="space-y-2">
                {finished.slice(0, 10).map((raffle) => (
                  <WinnerRow key={raffle.id} raffle={raffle} />
                ))}
              </div>
            </div>
          </aside>
        )}
      </div>
    </div>
  );
}

function WinnerRow({ raffle }: Readonly<{ raffle: RaffleListItem }>) {
  return (
    <Link
      to={`/raffles/${raffle.id}`}
      className="flex items-center gap-3 rounded-2xl border border-white/5 bg-surface/60 p-3 transition-colors hover:border-brand-pink/25"
    >
      <ProductImage src={raffle.product.images[0] ?? null} alt="" className="h-11 w-11 shrink-0 rounded-xl" />
      <div className="min-w-0 flex-1">
        <p className="truncate text-xs font-medium text-white/80">{raffle.title}</p>
        <p className="text-[10px] text-white/45">
          {raffle.winnerName ? `Won by ${raffle.winnerName}` : "Drawn"} · {raffle.ticketCount} tickets
        </p>
      </div>
      <Trophy size={16} className="shrink-0 text-amber-300" />
    </Link>
  );
}
